"use client";
import React, { memo } from "react";
import { Text } from "@dxp-feed/ui/Text";
import { Button } from "@dxp-feed/ui/Button";
import { Box } from "@dxp-feed/ui/Box";
import { Checkbox } from "@dxp-feed/ui/Checkbox";
import { Separator } from "@dxp-feed/ui/Separator";
import { Input } from "@dxp-feed/ui/Input";
import {
  useAppDispatch,
  useAppSelector,
  useEffect,
  useState,
  useRef,
} from "@/utils/hooks/reactUseCustomHooks";
import {
  createRecipeListAction,
  createRecipeToListAction,
} from "@/utils/redux/recipes/addtolistSlice/actions";
import {
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
} from "@dxp-feed/ui/Dialog";
import Link from "@/components/Link";
import { Card, CardHeader } from "@dxp-feed/ui/Card";
import { minToHourConverter } from "@/helpers/useCommonFunctions";
import ImageWrapper from "@/components/ImageWrapper";
import {
  addDataLayerOfRecipeToList,
  addDataLayerRecipeAddList,
} from "@/helpers/dataLayer";
import CreateNewList from "./CreateNewList";
import useValidation from "../common/validation";
import { setToastMessage } from "@/utils/redux/recipes/toastMessageSlice";
import { RecipeImage } from "@/utils/constant";
import { useGigyaToken } from "../common/commonRecipeFunctions/getGigyaToken";
import { useGigyaAccountInfo } from "@/helpers/gigyaCentralSsoHook";
import { setRecipeList } from "@/utils/redux/recipes/addtolistSlice";
import { sendSalesforceInteraction } from "@/helpers/cdnSdk";
import { getUTMParameter } from "@/helpers/getUTMParameters";
import { mealPlanDefautMessage, recipeModalData } from "@/utils/aemDefaultData";

interface RecipeList {
  id: string;
  list_name: string;
  recipes: any;
}

const CreateRecipesList = ({
  recipeData,
  recipeList,
  closeAllModal,
  hideAddToFavModal,
}: {
  recipeData: any;
  recipeList: any;
  closeAllModal: any;
  hideAddToFavModal: any;
}) => {
  console.log("CreateRecipesList ---------CreateRecipesList");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(true);
  const [recipeListName, setRecipeListName] = useState("");
  const [folderId, setFolderId] = useState<any>(null);
  const [validationError, setValidationError] = useState("");

  const [checked, setChecked] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const dispatch = useAppDispatch();
  const { getAccountInfo, resetLoginParams } = useGigyaToken();
  const gigyaToken = useGigyaAccountInfo();
  const [disableButtonsFolder, setDisableButtonsFolder] = useState(true);
  const [disableButtonRecipe, setDisableButtonRecipe] = useState(true);
  const [listNameAlreadyExist, setListNameAlreadyExist] = useState("");
  const [selectedRecipeList, setSelectedRecipeList] = useState<RecipeList>({
    id: "",
    list_name: "",
    recipes: [],
  });
  const [recipeByListId, setRecipeByListId] = useState<any>([recipeData]);
  const [type, setType] = useState<string | null>("Add");
  const [hide, setHide] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { messages: MESSAGE } = useAppSelector((state) => state.messageSlice);
  const { validateInput, valueWithoutInitalSpace } = useValidation();
  const { aemRecipeModelData: aemModalData } = useAppSelector(
    (state) => state.AemModalSlice
  );
  const { recipeList: recipeListData } = useAppSelector(
    (state) => state.RecipeToList
  );
  const { uid: Uid } = useAppSelector((state) => state.gigyaslice);

  useEffect(() => {
    if (isModalOpen && inputRef.current) {
      inputRef.current.focus(); // Automatically focus the input when the modal is open
    }
  }, [isModalOpen]); // Re-run when `isModalOpen` changes

  const openModal = () => {
    setIsModalOpen(true);
    hideAddToFavModal();
  };
  const closeModal = () => {
    setIsModalOpen(false);
    closeAllModal();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value,
      error: { regError, initialSpaceError },
    } = valueWithoutInitalSpace(e.target.value);
    setListNameAlreadyExist("");
    setRecipeListName(value);
    const error = validateInput(value, regError, initialSpaceError);
    setValidationError(error);
    if (value !== recipeListName) setDisableButtonsFolder(!!error);
  };

  const addedRecipetoList = (recipes: any) => {
    setRecipeByListId(recipes);
  };

  const saveRecipeListName = async () => {
    const { idToken: token } = await gigyaToken();
    const payload = {
      list_name: recipeListName,
      id: folderId,
      type: type,
      token,
    };
    setDisableButtonsFolder(true);
    const result: any = await dispatch(createRecipeListAction(payload));

    if (result?.payload?.status == 401) {
      resetLoginParams();
      return;
    }
    if (result?.payload?.data) {
      const { id, list_name } = result.payload.data;
      setSelectedRecipeList({
        id: id,
        list_name: list_name,
        recipes: [],
      });

      const newList = {
        id: id,
        list_name: list_name,
        recipes: [],
      };

      dispatch(setRecipeList([...recipeListData, newList]));
      addDataLayerRecipeAddList({
        event: "button_click",
        click_text: "create a list",
        list_name: recipeListName,
        page_title: document.title,
        page_type: "Recipe List",
      });
      sendSalesforceInteraction({
        name: "listCreated",
        banner: process.env.NEXT_PUBLIC_MERCHANT_IDENTIFIER,
        isAnonymous: Uid ? 0 : 1,
        gigyaUid: Uid,
        listName: recipeListName,
        screenName: document.title,
        screenClass: window.location.href,
        utmSource: getUTMParameter("utm_source"),
        utmMedium: getUTMParameter("utm_medium"),
        utmCampaign: getUTMParameter("utm_campaign"),
      });
      setFolderId(result?.payload?.data?.id);
      dispatch(setRecipeList([...recipeListData, newList]));
    }

    if (result.payload && result.payload.status == 409) {
      setListNameAlreadyExist(
        MESSAGE?.ERROR_MESSAGE?.RECIPE_LIST_ALREADY_EXIST ??
          mealPlanDefautMessage[0]?.recipeListAlreadyExist
      );
    } else {
      setListNameAlreadyExist("");
      setDisableButtonRecipe(false);
      setIsEditing(false);
    }
  };

  const toggleEditMode = () => {
    setIsEditing(true);
    setType("Edit");
    setDisableButtonRecipe(true);
  };

  useEffect(() => {
    if (recipeList && Array.isArray(recipeList) && recipeList.length > 0) {
      const initialChecked: any = recipeList
        .filter((item: any) =>
          item?.recipes?.some(
            (data: any) => data?.recipe_id === recipeData?.objectID
          )
        )
        .map((item: any) => item?.id);
      setChecked(initialChecked);
    } else {
      setChecked(false);
    }
  }, [recipeList, recipeData?.objectID]);

  const handleCheckboxChange = (isChecked: any) => {
    setChecked(isChecked);
  };

  const saveRecipesToFolder = async () => {
    if (!isDelete) {
      const recipes = [
        {
          recipe_id: recipeData.objectID,
          list_id: folderId,
          type: "save_list",
        },
      ];
      addDataLayerOfRecipeToList({
        event: "add_recipe_to_list",
        recipe_name: recipeData?.title,
        list_name: "Recipes",
        recipe_list_name: recipeListName,
        page_title: document.title,
      });
      const { idToken: token } = await gigyaToken();
      const payload = {
        token,
        recipes: recipes,
      };
      const result = await dispatch(createRecipeToListAction(payload));
      if (result?.payload?.status == 401) {
        resetLoginParams();
        return;
      }
      if (result?.payload?.status == 200) {
        const enrichedList = recipeListData.map((list: any) =>
          list.id === folderId
            ? {
                ...list,
                recipes: [...list.recipes, { recipe_id: recipeData.objectID }],
              }
            : list
        );
        dispatch(setRecipeList(enrichedList));
        dispatch(
          setToastMessage({
            message:
              MESSAGE?.RECIPE_ADDED_TO_LIST ??
              mealPlanDefautMessage[0]?.recipeSuccessfulAdd,
            status: "success",
            color: "success",
            messageOf: "recipe",
          })
        );
      }
      setFolderId(null);
      closeModal();
      closeAllModal(true);
    }
    setRecipeListName("");
    setIsEditing(true);
    closeModal();
  };

  const deleteRecipe = () => {
    setChecked(false);
    setIsDelete(true);
  };

  const hideCurrentModal = () => {
    setHide(true);
  };

  return (
    <>
      {/* Button to open modal */}
      <Box as="div">
        <Button
          variant="outline"
          className="gap-3 flex text-[14px] font-normal text-brandBlack min-w-[156px] py-[8.5px]"
          onClick={openModal}
          aria-label="Create New List"
        >
          {aemModalData?.chooseNewList ?? recipeModalData[0]?.chooseNewList}
          {/* Create New List */}
        </Button>
      </Box>
      {isModalOpen && (
        <Dialog
          isOpen={isModalOpen}
          onClose={closeModal}
          className={`max-w-full md:max-w-[708px] rounded-none md:rounded-lg p-0 ${hide && "hidden"}`}
          positionClass="items-end md:items-center "
          closeOnOverlayClick={false}
        >
          <DialogHeader className="border-grey350 border-b-2 py-6 md:py-4 px-6">
            <Box as="div" className="flex gap-1 flex-col">
              <Text as="p" className="text-[14px]">
                {aemModalData?.nameRecipeListTitle}
              </Text>
              {isEditing ? (
                <Box as="div" className="relative flex items-start">
                  <Box as="div" className="flex flex-col">
                    <label htmlFor="Type here" className="sr-only">
                      {aemModalData?.placeholderText}
                    </label>
                    <Input
                      id="Type here"
                      type="text"
                      autoComplete="off"
                      ref={inputRef} // Attach the ref to the input
                      value={recipeListName}
                      onChange={handleInputChange}
                      className="w-full border-grey350 rounded py-1 px-2 min-w-[170px] max-w-[170px] min-h-[40px] text-brandBlack placeholder-grey900 placeholder:font-normal placeholder:italic placeholder:text-[16px]"
                      placeholder={`${aemModalData?.placeholderText ?? recipeModalData[0]?.placeholderText }`}
                    />
                    {validationError && (
                      <Text
                        role="alert"
                        aria-live="assertive"
                        className="text-[12px] pt-[2px] text-red300 min-w-[170px] max-w-[170px]"
                      >
                        {validationError}
                      </Text>
                    )}
                    {listNameAlreadyExist && (
                      <Text className="text-[12px] pt-[2px] text-red300 min-w-[170px] max-w-[170px]">
                        {listNameAlreadyExist}
                      </Text>
                    )}
                  </Box>
                  <Button
                    variant="primary"
                    className="ml-2 text-[14px] font-light h-10 min-w-[83px]"
                    onClick={saveRecipeListName}
                    disabled={disableButtonsFolder}
                    aria-label="save"
                    // Dynamically disable/enable button
                  >
                    {aemModalData?.save ?? recipeModalData[0]?.save}
                    {/* Save */}
                  </Button>
                </Box>
              ) : (
                <Text
                  as="h2"
                  className="font-bold text-[20px] flex item-center"
                  aria-label={recipeListName}
                >
                  {recipeListName}
                  <Button
                    variant="icon"
                    size="none"
                    onClick={toggleEditMode}
                    aria-label="Edit My Recipe List"
                  >
                    <span className="demo-icon icon-edit pl-2 text-[18px] min-w-5 min-h-5 text-brandSecondary cursor-pointer"></span>
                  </Button>
                </Text>
              )}
            </Box>
            <Button
              className="text-brandSecondary font-bold text-lg h-[24px] rounded-none"
              onClick={closeModal}
              aria-label="Close modal"
              variant="icon"
              size="none"
            >
              <Text
                as="span"
                className="demo-icon icon-close text-brandBlack text-[14px] "
              ></Text>
            </Button>
          </DialogHeader>

          <DialogBody className="pl-1 pr-4 xs:pl-4 xs:pr-6 py-2 md:py-4 max-h-[262px] md:max-h-[270px] overflow-y-auto">
            {isDelete ? (
              <Box
                as="div"
                className="flex flex-col items-center gap-3 justify-center text-center py-4 px-6"
              >
                <ImageWrapper
                  src="/recipes/folder.svg"
                  width={80}
                  height={80}
                  className=""
                  alt="scene plus image"
                />
                <Text as="p" className="max-w-[300px] font-normal">
                  {aemModalData?.recipeEmptyListMessage ?? recipeModalData[0]?.recipeEmptyListMessage}
                  {/* This recipe list is empty. You can add new recipes now or save
                it as it is. */}
                </Text>
              </Box>
            ) : (
              <>
                {checked ? (
                  <Box as="div">
                    <Box as="div" className={`gap-4 justify-end flex`}>
                      <Button
                        onClick={() => deleteRecipe()}
                        variant="outline"
                        className="gap-3 text-[14px] group font-normal"
                        aria-label="Delete my recipe list"
                      >
                        <Text
                          as="span"
                          className="demo-icon icon-bin text-[20px] w-6 h-6 text-brandSecondary flex items-center justify-center group-hover:text-brandWhite group-focus:text-brandWhite"
                        ></Text>
                        {/* Delete{" "} */}
                      </Button>
                    </Box>
                    <Separator
                      orientation="horizontal"
                      className="bg-grey250 !h-[2px] my-4"
                    />
                  </Box>
                ) : null}
                <Box as="div" className="grid grid-cols-1 gap-2 w-[100%] ">
                  <Card className="flex shadow-none items-center overflow-visible ">
                    <Text
                      as="span"
                      className="w-12 h-12 flex items-center justify-center"
                    >
                      <Checkbox
                        checked={checked}
                        onChange={(e) => handleCheckboxChange(e.target.checked)}
                        aria-label="Accept terms and conditions"
                      />
                    </Text>
                    <Box
                      as="div"
                      className="relative flex gap-3 w-full rounded-[12px] bg-brandWhite shadow-[0px_0px_4px_0px_rgba(0,0,0,0.25)] overflow-hidden "
                    >
                      <Text
                        as="div"
                        className="relative bg-grey300 min-h-[72px] overflow-hidden  pl-[100px] xs:pl-[128px]"
                      >
                        <ImageWrapper
                          src={`${RecipeImage + recipeData?.featuredImage || recipeData?.featuredImage}`}
                          alt={`recipes`}
                          fill
                          className="absolute top-0 left-0 object-cover"
                        />
                      </Text>
                      <CardHeader className="border-none w-full py-2 xs:py-0 gap-1 xs:gap-3 flex flex-col xs:flex-row items-start xs:items-center justify-center xs:justify-between px-0 pr-2">
                        <Text
                          as="div"
                          className="text-[14px] font-normal [&>strong]:font-bold line-clamp-1 2xs:line-clamp-2"
                        >
                          {recipeData?.title}
                        </Text>
                        <Text as="ul" className="flex items-center">
                          <Text
                            as="li"
                            className="flex items-center justify-start xs:justify-end w-full xs:min-w-[110px]"
                          >
                            {recipeData?.cooking?.totalTime ? (
                              <>
                                <Text
                                  as="span"
                                  className="demo-icon icon-clock text-[19px] font-light flex items-center"
                                ></Text>
                                <Text
                                  as="span"
                                  className="py-0 px-2 text-[14px]"
                                >
                                  {" "}
                                  {minToHourConverter(
                                    recipeData?.cooking?.totalTime
                                  )}
                                </Text>
                              </>
                            ) : (
                              ""
                            )}
                          </Text>
                          <Text as="span">|</Text>
                          <Link
                            href={`/recipes/${recipeData?.pageSlug}`}
                            target="_blank"
                            aria-label={`View recipe for ${recipeData?.title}. Open in new tab`}
                          >
                            <Text
                              as="span"
                              className="demo-icon icon-third-party text-[22px] text-brandSecondary min-w-[48px] min-h-[20px] xs:min-h-[48px] flex items-center justify-center "
                            ></Text>
                          </Link>
                        </Text>
                      </CardHeader>
                    </Box>
                  </Card>
                </Box>
              </>
            )}
          </DialogBody>
          <DialogFooter className="border-grey350 border-t-2 py-6 md:py-4 pt-4 px-6 flex flex-col 2xs:flex-row w-full justify-center 2xs:justify-between gap-4 2xs:gap-6 ">
            <CreateNewList
              closePreviousModal={hideCurrentModal}
              addRecipeToList={true}
              selectedRecipeList={selectedRecipeList}
              recipeByListId={checked ? recipeByListId : []}
              addedRecipetoList={addedRecipetoList}
              closeFavModal={closeModal}
              buttonDisabled={selectedRecipeList.id !== ""}
              componentName={"favourite"}
            />
            <Button
              variant="primary"
              className="gap-3 flex text-[14px] font-normal h-10 group"
              onClick={saveRecipesToFolder}
              disabled={disableButtonRecipe || !!validationError}
              aria-label="Save Recipe List"
            >
              {aemModalData?.saveRecipeList}
            </Button>
          </DialogFooter>
        </Dialog>
      )}
    </>
  );
};

export default memo(CreateRecipesList);
